import { useNavigate } from 'react-router-dom'
import { Form, Field, Formik, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import apis from '../../libs/services'
import useCheckingEmail from '../../hooks/useCheckingEmail'
import { Amplify } from 'aws-amplify'
import { resetPassword } from 'aws-amplify/auth'
import DiamondWrapper from '../../components/global/diamongWrapper'
import AuthPageLayout from '../../components/global/AuthPageLayout'
import { Button, Input, PageTitle } from '../../components/atoms'
import { useAuth } from '../../hooks/useAuth'
import { useAuthContext } from '../../contexts/AuthContext'
import { ReactElement, useEffect } from 'react'
import useSetFocusById from '../../hooks/useSetFocus'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'
import ResetPasswordLink from '../../components/molecules/ResetPasswordLink'
import * as Sentry from '@sentry/react'
import { logger } from '../../utils'
import { getPostLoginDestination } from '../../utils/deeplink'

export const pageTitle = 'Log in Page'
export const idLoginPageMainContent = 'login-page-main-content'

interface FormValues {
  password: string
};

const initialValues = {
  password: ''
}

const LoginFormSchema = Yup.object().shape({
  password: Yup.string().required('This field is required!')
})

export enum FocusableElement {
  InputPassword = 'input-password',
  ButtonContinue = 'button-continue',
  ResetPassword = 'link-reset-password',
  LinkContact = 'link-contact',
}

function Login (): ReactElement {
  const navigate = useNavigate()
  const { email } = useCheckingEmail()
  const { setUserProfile } = useAuth()
  const { signin } = useAuthContext()
  const keyboardControls = useKeyboardControls()
  const [setFocusById] = useSetFocusById()

  useEffect(() => {
    setFocusById(FocusableElement.InputPassword)
  }, [setFocusById])

  const handleResetPassword = async (): Promise<void> => {
    try {
      if (!email) {
        logger.error('Email is required to reset password')
        return
      }
      const currentConfig = Amplify.getConfig()
      logger.log('Full Amplify configuration:')
      logger.log(JSON.stringify(currentConfig))
      const result = await resetPassword({ username: email })
      logger.log('Password reset request result:')
      logger.log(JSON.stringify(result))
      navigate('/password-reset', { state: { email } })
    } catch (error) {
      logger.error('Error requesting password reset:')
      logger.error(error as any)
      // You could add a more sophisticated error handling here
      alert('Failed to request password reset. Please try again.')
    }
  }

  const handleFormSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>): Promise<any> => {
    try {
      const res = await signin(String(email), values.password)
      if (res.isSignedIn === true) {
        const profileRes = await apis.getUserProfile()
        const profileData = profileRes?.data?.data
        const sentryId = profileData?.id ?? 'unknown' // Using nullish coalescing
        Sentry.setUser({
          id: sentryId
        })
        setUserProfile(profileData)
        if (profileData.user_profile.accepted_terms === true) {
          if (profileData.role.slug === 'admin') {
            navigate('/dashboard/all-assigned-students')
          } else {
            // ProtectedRoute's survey guard will redirect to survey if needed
            navigate(getPostLoginDestination())
          }
        } else {
          navigate('/terms-of-use')
        }
      }
    } catch (err: any) {
      actions.setSubmitting(false)
      const errorMessage = typeof err?.response?.data?.error_description === 'string'
        ? err.response.data.error_description
        : 'Password is not correct!'
      actions.setFieldError('password', errorMessage)
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <AuthPageLayout mainId={idLoginPageMainContent}>
      <section className='flex justify-center w-full md:w-auto'>
        <Formik
          initialValues={initialValues}
          validationSchema={LoginFormSchema}
          onSubmit={handleFormSubmit}
        >
          {({ errors, isSubmitting, touched }: FormikProps<FormValues>) => (
            <DiamondWrapper className='pb-[77px]'>
              <div className='p-[22px] flex flex-col items-center'>
                <div>
                  <PageTitle title={pageTitle} addScreenReaderH1={false} />
                  <h1 className='font-bold capitalize text-[20px] md:text-[26px] text-dopage-lightGreen text-center'>
                    log in
                  </h1>

                  <div className='w-full md:min-w-[440px] mt-[40px] md:mt-[80px]'>
                    <Form>
                      <Input className='w-full py-[20px] px-[18px] my-[10px] text-[20px] md:py-[30px] md:px-[27px] md:text-[26px]' aria-label='email' type='email' placeholder='Email' value={email} readOnly />
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.InputPassword)}
                        highlightEffect={HighlightEffect.InputOutline}
                        data-testid={`highlight-${FocusableElement.InputPassword}`}
                        activeClassName='mt-[26px] mb-[8px]'
                      >
                        <label aria-label='password'>
                          <Field name='password' type='password'>
                            {({
                              field
                            }: any) => (
                              <KeyboardControl>
                                <Input
                                  id={FocusableElement.InputPassword}
                                  data-testid={FocusableElement.InputPassword}
                                  className='w-full py-[20px] px-[18px] text-[20px] md:py-[30px] md:px-[27px] md:text-[26px] border-dopage-primary'
                                  type='password'
                                  placeholder='Password'
                                  {...field}
                                />
                              </KeyboardControl>
                            )}
                          </Field>
                        </label>
                      </ControlHighlight>
                      {(errors.password !== undefined &&
                        errors.password !== '' &&
                        touched.password === true)
                        ? (
                          <div
                            className='text-[#ff0000] pt-1'
                            aria-label={`Error message: ${errors.password}`}
                          >
                            {errors.password}
                          </div>
                          )
                        : null}
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonContinue)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                        data-testid={`highlight-${FocusableElement.ButtonContinue}`}
                      >
                        <KeyboardControl>
                          <Button
                            className='diamond-btn h-[64px] md:h-[86px] w-full font-bold mt-[32px] flex justify-center items-center'
                            data-testid={FocusableElement.ButtonContinue}
                            disabled={isSubmitting}
                            id={FocusableElement.ButtonContinue}
                            loading={isSubmitting}
                            type='submit'
                          >
                            <span className='text-black text-[20px] md:text-[26px] text-neutral-090'>Continue</span>
                          </Button>
                        </KeyboardControl>
                      </ControlHighlight>
                      <ResetPasswordLink
                        id={FocusableElement.ResetPassword}
                        onClick={handleResetPassword}
                      />
                      <ContactLinks
                        id={FocusableElement.LinkContact}
                      />
                    </Form>
                  </div>
                </div>
              </div>
            </DiamondWrapper>
          )}
        </Formik>
      </section>
    </AuthPageLayout>
  )
}

export default Login
